// src/pages/packingBatch/PrintFormats/A4FourPerPageLandscapeFormat.jsx
import React, { useEffect, useRef } from 'react';
import JsBarcode from 'jsbarcode';
import styles from './A4FourPerPageLandscapeFormat.module.css';
import { getImageUrl, handleImageError } from '../../../utils/imageUtils';

const A4FourPerPageLandscapeFormat = ({ printData }) => {
  const barcodeRefs = useRef({});

  useEffect(() => {
    // Generate barcodes for all airwaybill numbers
    printData.orders.forEach((order) => {
      const barcodeElement = barcodeRefs.current[order.airwaybillNumber];
      if (barcodeElement && order.airwaybillNumber) {
        try {
          JsBarcode(barcodeElement, order.airwaybillNumber, {
            format: 'CODE128',
            width: 2,
            height: 40,
            displayValue: true,
            fontSize: 11,
            margin: 3,
            background: '#ffffff',
            lineColor: '#000000'
          });
        } catch (error) {
          console.error('Failed to generate barcode:', error);
        }
      }
    });
  }, [printData]);

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const formatPhone = (phones) => {
    if (!phones || phones.length === 0) return 'N/A';
    return phones.slice(0, 2).join(', ');
  };

  const truncateText = (text, maxLength) => {
    if (!text) return '';
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
  };

  // Group orders into pages of 4
  const groupIntoPages = (orders) => {
    const pages = [];
    for (let i = 0; i < orders.length; i += 4) {
      pages.push(orders.slice(i, i + 4));
    }
    return pages;
  };

  const pages = groupIntoPages(printData.orders);

  return (
    <div className={styles.a4Container}>
      {pages.map((pageOrders, pageIndex) => (
        <div key={pageIndex} className={styles.page}>
          <div className={styles.grid}>
            {pageOrders.map((order, orderIndex) => (
              <div key={order.orderId} className={styles.airwaybill}>
                {/* Header */}
                <div className={styles.header}>
                  <div className={styles.shopSection}>
                    {order.shop.logo ? (
                      <img 
                        src={getImageUrl(order.shop.logo)} 
                        alt={order.shop.name}
                        className={styles.shopLogo}
                        onError={(e) => handleImageError(e)}
                      />
                    ) : (
                      <div className={styles.shopLogoPlaceholder}>
                        {order.shop.name.charAt(0)}
                      </div>
                    )}
                    <div className={styles.shopInfo}>
                      <div className={styles.shopName}>{truncateText(order.shop.name, 20)}</div>
                      {order.shop.phone && (
                        <div className={styles.shopContact}>{order.shop.phone}</div>
                      )}
                    </div>
                  </div>
                  <div className={styles.courierSection}>
                    <div className={styles.courierName}>
                      {printData.batch?.account?.name || printData.batch?.courier?.name || order?.courierAccount?.name || 'N/A'}
                    </div>
                    <div className={styles.orderDate}>
                      {formatDate(order.orderDate)}
                    </div>
                  </div>
                </div>

                {/* Order Number with URGENT badge */}
                <div className={styles.orderNumberRow}>
                  <div className={styles.orderNumber}>
                    Order #{order.orderNumber}
                    {order.privateReference && (
                      <span className={styles.privateReference}> ({order.privateReference})</span>
                    )}
                  </div>
                  {order.isUrgent && (
                    <div className={styles.urgentBadge}>URGENT</div>
                  )}
                </div>

                {/* Main Content - Two columns */}
                <div className={styles.mainContent}>
                  {/* Left Column - Delivery Information */}
                  <div className={styles.leftColumn}>
                    <div className={styles.deliverySection}>
                      <div className={styles.sectionTitle}>DELIVER TO</div>
                      <div className={styles.customerName}>{order.customer.name}</div>
                      <div className={styles.phone}>📞 {formatPhone(order.customer.allPhones)}</div>
                      <div className={styles.address}>{truncateText(order.customer.address, 75)}</div>
                      <div className={styles.location}>
                        📍 {order.customer.city}
                        {order.customer.district && ` (${order.customer.district})`}
                      </div>
                    </div>

                    {/* Delivery Notes */}
                    {order.deliveryNotes && (
                      <div className={styles.notesSection}>
                        <div className={styles.notesLabel}>📝 Note:</div>
                        <div className={styles.notes}>
                          {truncateText(order.deliveryNotes, 60)}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Right Column - Order Details */}
                  <div className={styles.rightColumn}>
                    {/* Order Items */}
                    <div className={styles.itemsSection}>
                      <div className={styles.sectionTitle}>ITEMS ({order.itemsCount})</div>
                      <div className={styles.items}>
                        {order.items.slice(0, 3).map((item, idx) => (
                          <div key={idx} className={styles.item}>
                            • {item.itemDisplayStr}
                          </div>
                        ))}
                        {order.items.length > 3 && (
                          <div className={styles.moreItems}>
                            +{order.items.length - 3} more
                          </div>
                        )}
                      </div>
                    </div>

                    {/* COD Amount */}
                    <div className={styles.codSection}>
                      <div className={styles.codAmount}>
                        Rs. {order.financials.codAmount.toLocaleString()}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Barcode */}
                <div className={styles.barcodeSection}>
                  <svg
                    ref={(el) => (barcodeRefs.current[order.airwaybillNumber] = el)}
                    className={styles.barcode}
                  />
                </div>

                {/* Footer with warehouse info */}
                {(printData.batch?.warehouse?.name || order?.warehouse?.name) && (
                  <div className={styles.footer}>
                    {printData.batch?.warehouse?.name || order?.warehouse?.name}
                  </div>
                )}
              </div>
            ))}
            
            {/* Fill empty slots if less than 4 orders on last page */}
            {pageOrders.length < 4 && Array.from({ length: 4 - pageOrders.length }).map((_, idx) => (
              <div key={`empty-${idx}`} className={styles.emptySlot} />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
};

export default A4FourPerPageLandscapeFormat;